import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../components/AuthProvider';
import { MultiCampaignDashboard } from '../components/MultiCampaignDashboard';
import { CONNECTION_STATES } from '../contexts/WebSocketContext';
import config from '../config';

const DashboardPage = () => {
  const { connectionState, error: wsError } = useWebSocket();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const initializeDashboard = useCallback(async () => {
    try {
      if (!isAuthenticated || !user) {
        console.log('Not authenticated, redirecting to login');
        navigate('/login');
        return;
      }

      // Debug logging only in development and without exposing credential values
      if (process.env.NODE_ENV === 'development') {
        console.log('Auth state:', { isAuthenticated, user });
        console.log('DashboardPage: google_credentials present (localStorage):', !!localStorage.getItem('google_credentials'));
        console.log('DashboardPage: google_credentials present (sessionStorage):', !!sessionStorage.getItem('google_credentials'));
        console.log('Connection state:', connectionState);
      }

      // Connection is now handled automatically by UnifiedMessaging
      // when the component mounts with a valid token
      setIsInitialized(true);
      setLoading(false);
    } catch (err) {
      console.error('Dashboard initialization error:', err);
      setError(err);
      setLoading(false);
    }
  }, [isAuthenticated, user, navigate, connectionState]);

  // Initialize dashboard on mount and auth changes
  useEffect(() => {
    if (!isInitialized) {
      initializeDashboard();
    }
  }, [isInitialized, initializeDashboard]);

  // Handle WebSocket errors
  useEffect(() => {
    if (wsError) {
      setError(wsError);
    }
  }, [wsError]);

  // Handle OAuth callback and trigger sync immediately
  useEffect(() => {
    const checkOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const isConnected = urlParams.get('connected');
      const toolkitSlug = urlParams.get('toolkit');

      // Check sessionStorage as fallback
      const sessionConnected = sessionStorage.getItem('workhub_connected');
      const sessionToolkit = sessionStorage.getItem('workhub_toolkit');

      const finalConnected = isConnected || sessionConnected;
      const finalToolkit = toolkitSlug || sessionToolkit;

      if ((finalConnected === 'true' || finalConnected === true) && finalToolkit) {
        console.log(`✅ Successfully connected to ${finalToolkit}!`);

        // Clear URL params immediately
        window.history.replaceState({}, document.title, '/dashboard');
        sessionStorage.removeItem('workhub_connected');
        sessionStorage.removeItem('workhub_toolkit');
        console.log('🔄 Cleared URL parameters and session storage');

        // Show success message (using alert to match existing modal behavior)
        setTimeout(() => {
          alert(`Successfully connected to ${finalToolkit}! Syncing data...`);
        }, 500);

        // Trigger on-demand sync
        try {
          const token = localStorage.getItem('auth_token') || 
                       localStorage.getItem('token') || 
                       localStorage.getItem('jwt_token');
          const userStr = localStorage.getItem('user');
          const userEmail = userStr ? JSON.parse(userStr).email : null;

          console.log('🔑 Auth state:', { 
            hasToken: !!token, 
            hasUserEmail: !!userEmail,
            apiUrl: config.apiUrl 
          });

          if (userEmail && token) {
            const syncUrl = `${config.apiUrl}/sync/on-demand`;
            console.log(`🚀 Triggering on-demand sync for:`, { 
              toolkit: finalToolkit,
              endpoint: syncUrl 
            });

            const response = await fetch(syncUrl, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                user_email: userEmail,
                toolkit: finalToolkit,
                upload_to_supabase: true
              })
            });

            console.log('📥 Sync response status:', response.status);
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }
            const data = await response.json();
            console.log('✅ Sync successful:', data);

            // Show completion message
            setTimeout(() => {
              alert('Sync completed successfully!');
            }, 1000);
          } else {
            console.error('❌ Missing required data for sync:', {
              hasToken: !!token,
              hasUserEmail: !!userEmail
            });
            setTimeout(() => {
              alert('Sync failed: Missing authentication data. Please try again.');
            }, 1000);
          }
        } catch (error) {
          console.error('❌ Sync error:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          setTimeout(() => {
            alert('Sync failed. Please try again.');
          }, 1000);
        }
      }
    };

    checkOAuthCallback();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Removed disconnect function usage
    };
  }, []);

  // Render loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading Dashboard</h2>
          <p className="text-gray-500">
            {connectionState === CONNECTION_STATES.CONNECTING 
              ? 'Establishing connection...'
              : 'Loading your dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-red-500 text-xl">
            <h2>Error Loading Dashboard</h2>
            <p>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Render multi-campaign dashboard
  return (
    <div className="dashboard-page">
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>Error: {error.message}</div>
      ) : (
        <MultiCampaignDashboard />
      )}
    </div>
  );
};

export default DashboardPage;